// Story 8.1 (ADO-2010), Task 8.4: the landing-page read.
//
// `GET /api/v1/landing?type=` (content-management `LandingController`, JwtAuthGuard)
// returns the tenant's landing tiles for one Trading Account type. Each tile carries
// `productQuery`: the exact query params the product list sends to
// `GET /api/v1/catalogue/products`, already string-valued (list params comma-joined).
// Hand-duplicated from the backend `LandingPageDto`
// (`libs/modules/content-management/src/contracts/dtos/landing-page.dto.ts`); keep
// the two in lock-step. Throws on failure so the page can show a retry; an empty
// landing is a 200 with `tiles: []`, never an error.

import { API_PREFIX, apiFetch } from './shoplink-api';

export const LANDING_PATH = `${API_PREFIX}/landing`;

export type LandingType = 'FOODSERVICE' | 'LICENSED_TRADE' | 'LSN';

export interface LandingTile {
  /** Stable id (the React key). */
  readonly key: string;
  readonly name: string;
  /** Same-origin path of the tile's image (`/images/landing/...`); `null`: text-only tile. */
  readonly imageUrl: string | null;
  /** The product-list query params the tile opens with. */
  readonly productQuery: Readonly<Record<string, string>>;
}

export interface LandingPage {
  readonly type: LandingType;
  readonly tiles: readonly LandingTile[];
  /** FOODSERVICE only; the section is hidden when empty. */
  readonly globalFavourites: readonly LandingTile[];
}

/**
 * The web-only URL param that carries a hierarchy tile's name to the product list, so
 * the active-filter chip can name the scope. The product list never sends it to the API.
 */
export const SCOPE_LABEL_PARAM = 'scopeLabel';

function parseTile(value: unknown): LandingTile | null {
  if (value === null || typeof value !== 'object') return null;
  const { key, name, imageUrl, productQuery } = value as Record<string, unknown>;
  if (typeof key !== 'string' || key === '' || typeof name !== 'string') return null;
  if (productQuery === null || typeof productQuery !== 'object') return null;
  const query: Record<string, string> = {};
  for (const [param, paramValue] of Object.entries(productQuery)) {
    if (typeof paramValue !== 'string') return null;
    query[param] = paramValue;
  }
  return {
    key,
    name,
    imageUrl: typeof imageUrl === 'string' && imageUrl !== '' ? imageUrl : null,
    productQuery: query,
  };
}

function parseTiles(value: unknown): LandingTile[] {
  return Array.isArray(value)
    ? value.map(parseTile).filter((tile): tile is LandingTile => tile !== null)
    : [];
}

export async function fetchLandingPage(type: LandingType): Promise<LandingPage> {
  const res = await apiFetch(`${LANDING_PATH}?type=${encodeURIComponent(type)}`);
  if (!res.ok) throw new Error(`Failed to load the landing page (${res.status})`);
  const body = (await res.json()) as Record<string, unknown>;
  return {
    type,
    tiles: parseTiles(body.tiles),
    globalFavourites: parseTiles(body.globalFavourites),
  };
}

/**
 * The product-list route a tile opens: its `productQuery` verbatim, plus the tile name
 * as {@link SCOPE_LABEL_PARAM} for a hierarchy tile (an LSN tile's chip already names
 * its category, so it carries no label).
 */
export function productListHref(tile: LandingTile): string {
  const params = new URLSearchParams(tile.productQuery);
  if (params.has('hierarchyCode') && tile.name !== '') params.set(SCOPE_LABEL_PARAM, tile.name);
  return `/products?${params.toString()}`;
}
